import { useRef, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from '@/i18n'
import type { Editor } from '@tldraw/tldraw'

interface CommentModeOverlayProps {
  isActive: boolean
  editor: Editor | null
  onPositionClick: (screenX: number, screenY: number, canvasX: number, canvasY: number) => void
  onExitMode: () => void
}

const CommentModeOverlay = ({
  isActive,
  editor,
  onPositionClick,
  onExitMode,
}: CommentModeOverlayProps) => {
  const { t } = useTranslation()
  const overlayRef = useRef<HTMLDivElement>(null)

  // Handle escape key to exit comment mode
  useEffect(() => {
    if (!isActive) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onExitMode()
        toast.info(
          t('board:comments.mode_cancelled', { defaultValue: 'Comment mode cancelled' })
        )
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isActive, onExitMode])

  // UI-57: while comment mode is on, this overlay covers the whole canvas at
  // z-index 10000 and swallows every press — including one landing squarely on
  // an EXISTING pin. So the pin could not be opened, and an attempt to drag it
  // dropped a second comment on top of the first. Placing a comment and
  // handling the comments already there were mutually exclusive.
  //
  // Over a pin the overlay stands aside (pointer-events: none) and the press
  // reaches the pin, which is what lets it be opened and dragged. Everywhere
  // else it still places a new comment.
  //
  // The hit-test listens on WINDOW rather than on the overlay: once the overlay
  // is pointer-transparent it stops receiving its own pointermove, so an
  // overlay-bound listener could turn itself off and never back on.
  const [overPin, setOverPin] = useState(false)

  useEffect(() => {
    if (!isActive || !editor) {
      setOverPin(false)
      return
    }
    const onMove = (e: PointerEvent) => {
      const page = editor.screenToPage({ x: e.clientX, y: e.clientY })
      const hit = editor
        .getShapesAtPoint(page, { hitInside: true })
        .some((shape) => shape.type === 'CommentPin')
      setOverPin(hit)
    }
    window.addEventListener('pointermove', onMove)
    return () => window.removeEventListener('pointermove', onMove)
  }, [isActive, editor])

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isActive || !editor) return

    const screenX = e.clientX
    const screenY = e.clientY

    // Convert screen coordinates to page (canvas) coordinates
    const pagePoint = editor.screenToPage({ x: screenX, y: screenY })

    // Belt and braces for the pointer-events dance above: a click can arrive
    // without a preceding pointermove (touch, or the cursor already sitting on
    // a pin when the mode is switched on), and placing a comment on top of one
    // that is already there is the exact failure being fixed.
    const onExistingPin = editor
      .getShapesAtPoint(pagePoint, { hitInside: true })
      .some((shape) => shape.type === 'CommentPin')
    if (onExistingPin) return

    e.preventDefault()
    e.stopPropagation()

    // Notify parent of clicked position
    onPositionClick(screenX, screenY, pagePoint.x, pagePoint.y)
  }

  if (!isActive) return null

  return (
    <div
      ref={overlayRef}
      onClick={handleClick}
      style={{
        position: 'absolute',
        top: 0,
        left: '150px', // Offset to avoid covering toolbar on left
        right: 0,
        bottom: 0,
        zIndex: 10000,
        cursor: overPin ? 'grab' : 'crosshair',
        pointerEvents: overPin ? 'none' : 'all',
      }}
      title={t('board:comments.overlay_click_to_place', {
        defaultValue: 'Click to place comment (ESC to cancel)',
      })}
    />
  )
}

export default CommentModeOverlay
